import { useState } from "react";
import { Play } from "lucide-react";

const EMBEDS = [
  [/instagram\.com\/(?:reel|reels|tv)\/([\w-]+)/i, (id) => `https://www.instagram.com/reel/${id}/embed/`],
  [/instagram\.com\/p\/([\w-]+)/i, (id) => `https://www.instagram.com/p/${id}/embed/captioned/`],
  [/tiktok\.com\/.*\/video\/(\d+)/i, (id) => `https://www.tiktok.com/embed/v2/${id}`],
  [/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]+)/i, (id) => `https://www.youtube.com/embed/${id}`],
];

export const embedUrl = (url = "") => {
  for (const [pattern, build] of EMBEDS) {
    const match = url.match(pattern);
    if (match) return build(match[1]);
  }
  return null;
};

const isFile = (url = "") => /\.(mp4|webm|mov)$/i.test(url);
const isImage = (url = "") => /\.(jpg|jpeg|png|webp|gif|avif)$/i.test(url);

/**
 * Plays a hosted /brand file or embeds an Instagram / TikTok / YouTube link.
 * Vertical 9:16 by default so it matches how the footage was shot.
 */
export const VideoEmbed = ({
  url,
  poster,
  aspect = "9/16",
  testId = "video",
  autoPlay = false,
  className = "",
}) => {
  const [started, setStarted] = useState(autoPlay);
  const embed = embedUrl(url);

  if (!url)
    return (
      <div
        data-testid={`${testId}-empty`}
        className={`flex items-center justify-center border border-dashed border-border bg-[#141414] ${className}`}
        style={{ aspectRatio: aspect }}
      >
        <span className="mono text-[10px] tracking-widest text-muted-foreground">
          FOOTAGE COMING
        </span>
      </div>
    );

  if (embed)
    return started ? (
      <iframe
        data-testid={`${testId}-iframe`}
        src={`${embed}${embed.includes("?") ? "&" : "?"}autoplay=1`}
        title="Smash story"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        scrolling="no"
        className={`w-full border-0 bg-black ${className}`}
        style={{ aspectRatio: aspect }}
      />
    ) : (
      <button
        data-testid={`${testId}-play`}
        onClick={() => setStarted(true)}
        className={`group relative w-full overflow-hidden bg-black ${className}`}
        style={{ aspectRatio: aspect }}
      >
        {poster ? (
          <img src={poster} alt="" className="h-full w-full object-cover opacity-70" />
        ) : (
          <div className="photo-band h-full w-full opacity-40" style={{ backgroundImage: "url(/brand/art-graffiti-color.jpg)" }} />
        )}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform duration-300 group-hover:scale-110">
            <Play size={24} fill="currentColor" />
          </span>
        </span>
      </button>
    );

  if (isImage(url))
    return (
      <img
        data-testid={`${testId}-image`}
        src={url}
        alt=""
        loading="lazy"
        className={`w-full object-cover ${className}`}
        style={{ aspectRatio: aspect }}
      />
    );

  if (isFile(url)) {
    const webm = url.endsWith(".mp4") ? url.replace(/\.mp4$/, ".webm") : null;
    return (
      <video
        data-testid={`${testId}-file`}
        controls
        playsInline
        preload="metadata"
        poster={poster || undefined}
        className={`w-full bg-black object-cover ${className}`}
        style={{ aspectRatio: aspect }}
        {...(webm ? {} : { src: url })}
      >
        {webm ? <source src={webm} type="video/webm" /> : null}
        {webm ? <source src={url} type="video/mp4" /> : null}
      </video>
    );
  }

  return (
    <a
      data-testid={`${testId}-link`}
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center justify-center border border-border bg-[#141414] ${className}`}
      style={{ aspectRatio: aspect }}
    >
      <span className="mono text-[10px] tracking-widest text-primary">WATCH →</span>
    </a>
  );
};

/** Muted, looping, decorative background footage. */
export const AmbientVideo = ({ url, poster, className = "" }) => {
  if (!url) return null;
  const webm = url.endsWith(".mp4") ? url.replace(/\.mp4$/, ".webm") : null;
  return (
    <video
      data-testid="ambient-video"
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      poster={poster || undefined}
      className={className}
      {...(webm ? {} : { src: url })}
    >
      {webm ? <source src={webm} type="video/webm" /> : null}
      {webm ? <source src={url} type="video/mp4" /> : null}
    </video>
  );
};
